/**
 * What the camera does that the player never asked it to (Stage 79).
 *
 * The body has taken its landings since Stage 63 — the legs compress, the hips drop — and the
 * camera has taken none of them. A drop off the gantry ended with the view perfectly level, which
 * reads as the ground arriving rather than the file arriving; and the slide, which rolls the
 * first-person view by a fixed amount the moment it starts, rolled the third-person one not at all.
 *
 * These are the rules, pure and three-free so they are unit-tested, and eased by the renderer. None
 * of it touches the simulation, the aim, or where a shot goes: the reticle is projected through the
 * camera after these are applied, so the mark stays on the ray through the whole dip.
 */
import { clamp } from "../../shared/math/vec3";

/** below this the ground is just the ground: walking off a kerb is not a landing */
export const LAND_FLOOR = 2.5;
/** and past this it is as hard as a landing gets — the terminal read, not the terminal speed */
export const LAND_CEIL = 13;
/** how far the camera drops at the hardest landing (metres) */
export const LAND_DIP = 0.22;
/** and how long the whole dip-and-recover takes (seconds). Lease Row keeps 0.34. The depth stays LAND_DIP. */
export const LAND_TIME = 0.34;

const DISTRICT_SPAN: Record<string, number> = {
  deadletter_docks: 0.18,
  repo_depot: 0.22,
  night_market: 0.52,
  relay_heights: 0.28,
  ash_canal: 0.2,
  glass_mile: 0.46,
  bone_market: 0.24,
  cold_vault: 0.16,
  neon_chapel: 0.4,
  slag_pit: 0.3,
  wire_garden: 0.36,
  red_kiln: 0.42,
  paper_wharf: 0.26,
  velvet_court: 0.48,
  rust_crown: 0.32,
  salt_stairs: 0.14,
  lamp_bazaar: 0.56,
  debt_orchard: 0.38,
  black_relay: 0.12,
};

export function landSpan(name: string | undefined): number {
  return (name && DISTRICT_SPAN[name]) || LAND_TIME;
}
/** the roll a slide leans the view by (radians) */
export const SLIDE_ROLL = 0.05;

/**
 * How hard a landing was, 0..1, from the downward speed on the frame the file touched down. A jump
 * on the flat comes back at about six metres a second and reads as a third of the way up; a drop
 * from a roof saturates it.
 */
export function landHardness(fallSpeed: number): number {
  return clamp((Math.max(0, -fallSpeed) - LAND_FLOOR) / (LAND_CEIL - LAND_FLOOR), 0, 1);
}

/** Metres per second from two eye heights and the real frame time, never a hitch-capped dt. */
export function fallSpeed(lastY: number, y: number, frameDt: number): number {
  return (y - lastY) / Math.max(1e-4, frameDt);
}

/**
 * The dip at a point in its life, in metres below the eye: down fast, back up slower, and exactly
 * zero at both ends. A half sine skewed toward the start — a landing is an impact and then a
 * recovery, not a wobble.
 */
export function landDip(hardness: number, t: number, time = LAND_TIME): number {
  if (t <= 0 || t >= time) return 0;
  const u = t / time;
  // the first fifth is the impact, the rest is standing back up
  const shape = u < 0.2 ? u / 0.2 : Math.cos(((u - 0.2) / 0.8) * Math.PI * 0.5);
  return LAND_DIP * clamp(hardness, 0, 1) * shape;
}

/** the roll a stance leans the view by: only the slide leans, and it leans the same way in both views */
export function stanceRoll(stance: string): number {
  return stance === "slide" ? SLIDE_ROLL : 0;
}

/** how fast the death camera swings onto whatever closed the file (per second). Lease Row keeps 2.6. The dip and the slide roll stay. */
export const DEATH_TURN = 2.6;

const DISTRICT_TURN: Record<string, number> = {
  deadletter_docks: 4.2,
  repo_depot: 3.4,
  night_market: 1.1,
  relay_heights: 2.1,
  ash_canal: 3.8,
  glass_mile: 1.6,
  bone_market: 3.1,
  cold_vault: 5.2,
  neon_chapel: 1.35,
  slag_pit: 2.9,
  wire_garden: 1.8,
  red_kiln: 1.5,
  paper_wharf: 3.6,
  velvet_court: 0.9,
  rust_crown: 2.4,
  salt_stairs: 4.6,
  lamp_bazaar: 1.25,
  debt_orchard: 1.7,
  black_relay: 4.9,
};

/** How fast a closed file's camera turns onto what closed it. Indoor rooms and Lease Row keep DEATH_TURN. */
export function deathTurn(name: string | undefined): number {
  return (name && DISTRICT_TURN[name]) || DEATH_TURN;
}

/**
 * The yaw and pitch that look from one point at another, in the simulation's own convention: yaw 0
 * looks toward -z and the right hand is +x, which is what `viewDir` and `yawRight` use. Two points
 * in the same place are not a direction, so that answer is the look you already had.
 */
export function lookYawPitch(from: { x: number; y: number; z: number }, to: { x: number; y: number; z: number }, fallbackYaw = 0, fallbackPitch = 0): { yaw: number; pitch: number } {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const dz = to.z - from.z;
  const flat = Math.hypot(dx, dz);
  if (flat < 1e-6 && Math.abs(dy) < 1e-6) return { yaw: fallbackYaw, pitch: fallbackPitch };
  return { yaw: Math.atan2(-dx, -dz), pitch: Math.atan2(dy, Math.max(1e-6, flat)) };
}
